/**
 * Role-related types for the multi-role user system
 */

export type UserRole = "sponsor_admin" | "sme" | "member" | "testing_member"

/** Test user role that initializes demo data */
export const TESTING_MEMBER_ROLE: UserRole = "testing_member"

/**
 * Maps the abbreviated role codes returned by the backend's login endpoint
 * (e.g. "SM") to their full UserRole equivalents (e.g. "sme").
 */
export const COGNITO_ROLE_MAP: Readonly<Record<string, UserRole>> = {
  SA: "sponsor_admin",
  SM: "sme",
  ME: "member",
  TM: "testing_member",
}

/**
 * Converts a backend abbreviated role code (e.g. "SM") to a UserRole.
 * Returns undefined for unknown or absent codes.
 */
export function mapCognitoRoleCode(code: string | undefined): UserRole | undefined {
  if (!code) return undefined
  return COGNITO_ROLE_MAP[code]
}

/**
 * Determines the role to use when auto-logging in after authentication.
 * PMs (sponsor_admin) and Experts (sme) start in the End User - Test role (testing_member)
 * so they experience the app as a participant by default.
 * End Users (member) stay in their own role.
 */
export function getLoginRole(defaultRole: UserRole | undefined): UserRole | undefined {
  if (defaultRole === "sponsor_admin" || defaultRole === "sme") {
    return TESTING_MEMBER_ROLE
  }
  return defaultRole
}

export interface RoleInfo {
  role: UserRole
  user_id: string
  is_active: boolean
}

export interface UserRoleInfoResponse {
  current_role: UserRole
  current_user_id: string
  available_roles: RoleInfo[]
  main_user_id: string
}

export interface RoleSwitchRequest {
  target_role: UserRole
}

export interface RoleSwitchResponse {
  target_role: UserRole
  target_user_id: string
  previous_user_id: string
  roles: {
    sponsor_admin_access: boolean
    sme_access: boolean
    member_access: boolean
    testing_member: boolean
  }
}

export interface RoleResetResponse {
  message: string
  user_id: string
}

export interface RolePermissions {
  sponsor_admin_access: boolean
  sme_access: boolean
  member_access: boolean
  testing_member: boolean
}
